/**
 * Campaign review detail (v1-2-5) — route /admin/campaigns/:id.
 *
 * Shows the campaign + its agreement status, renders ONLY the lifecycle actions
 * the backend reports as allowed (GET allowed-transitions), and exposes the
 * epic-7 "record signed agreement" / "void" controls. Destructive actions
 * (reject / cancel / go-live / end) confirm first. Backend guard failures (e.g.
 * go-live blocked because no signed agreement is recorded) are surfaced verbatim.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { AdminShell } from '../components/AdminShell';
import { ApiError, type CampaignRead } from '../lib/api';
import {
  applyTransition,
  CHECKLIST_ITEMS,
  DESTRUCTIVE_VERBS,
  downloadCampaignLeadsCsv,
  getAgreement,
  getAllowedTransitions,
  getCampaign,
  getCampaignLeads,
  getReview,
  OPERATOR_VERBS,
  recordAgreement,
  recordLeadConsent,
  revokeLeadConsent,
  updateReviewChecklist,
  updateSetupConfig,
  VERB_LABELS,
  voidAgreement,
  type AgreementRead,
  type LeadExportItem,
  type ReviewChecklist,
} from '../lib/adminApi';

const EMPTY_CHECKLIST: ReviewChecklist = {
  brand_fit_ok: false,
  prize_legitimacy_ok: false,
  prize_availability_ok: false,
  prize_value_ok: false,
  feasibility_ok: false,
  compliance_ok: false,
  fulfilment_ok: false,
};

function Banner({ kind, children }: { kind: 'error' | 'ok'; children: React.ReactNode }) {
  const cls =
    kind === 'error'
      ? 'border-red-200 bg-red-50 text-red-700'
      : 'border-emerald-200 bg-emerald-50 text-emerald-700';
  return (
    <div role="alert" className={`rounded-lg border px-4 py-2 text-sm ${cls}`}>
      {children}
    </div>
  );
}

// Copyable public competition URL (/win/<slug>) — shareable with the partner.
function CopyLink({ url }: { url: string }) {
  const [copied, setCopied] = useState(false);

  async function onCopy() {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard may be unavailable (insecure context); ignore — the link is
      // still selectable/openable.
    }
  }

  return (
    <div className="flex items-center gap-2">
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="max-w-[16rem] truncate text-primary hover:underline"
        title={url}
      >
        {url}
      </a>
      <button
        type="button"
        onClick={onCopy}
        className="rounded-md border border-line px-2 py-0.5 text-xs font-medium text-ink-700 hover:bg-surface-muted"
      >
        {copied ? 'Copied' : 'Copy'}
      </button>
    </div>
  );
}

// Review sign-off checklist (v1-2-4 backend; UI added hotfix-1-2). All seven
// must be confirmed + saved before the backend allows Approve.
function ReviewChecklistForm({
  campaignId,
  onSaved,
}: {
  campaignId: string;
  onSaved: () => void;
}) {
  const [state, setState] = useState<ReviewChecklist | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [savedOk, setSavedOk] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    getReview(campaignId)
      .then((r) => {
        if (!live) return;
        setState({
          brand_fit_ok: r.brand_fit_ok,
          prize_legitimacy_ok: r.prize_legitimacy_ok,
          prize_availability_ok: r.prize_availability_ok,
          prize_value_ok: r.prize_value_ok,
          feasibility_ok: r.feasibility_ok,
          compliance_ok: r.compliance_ok,
          fulfilment_ok: r.fulfilment_ok,
        });
      })
      .catch(() => live && setState({ ...EMPTY_CHECKLIST }));
    return () => {
      live = false;
    };
  }, [campaignId]);

  if (!state) return <p className="text-sm text-ink-500">Loading checklist…</p>;

  const allChecked = CHECKLIST_ITEMS.every((i) => state[i.key]);

  function toggle(key: keyof ReviewChecklist) {
    setState((s) => (s ? { ...s, [key]: !s[key] } : s));
    setSavedOk(false);
  }

  async function save() {
    if (!state) return;
    setBusy(true);
    setError(null);
    try {
      await updateReviewChecklist(campaignId, state);
      setSavedOk(true);
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save the checklist.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-3">
      <p className="text-xs text-ink-500">
        Confirm all seven, then Save — Approve is blocked until every item is checked.
      </p>
      <div className="grid gap-2 sm:grid-cols-2">
        {CHECKLIST_ITEMS.map((i) => (
          <label key={i.key} className="flex items-center gap-2 text-sm text-ink">
            <input
              type="checkbox"
              checked={state[i.key]}
              onChange={() => toggle(i.key)}
              className="h-4 w-4 rounded border-line text-primary focus:ring-primary"
            />
            {i.label}
          </label>
        ))}
      </div>
      {error && <Banner kind="error">{error}</Banner>}
      {savedOk && (
        <Banner kind="ok">
          {allChecked ? 'Checklist complete — you can approve.' : 'Saved.'}
        </Banner>
      )}
      <button
        type="button"
        onClick={save}
        disabled={busy}
        className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-60"
      >
        {busy ? 'Saving…' : 'Save checklist'}
      </button>
    </div>
  );
}

function AgreementForm({
  campaignId,
  onRecorded,
}: {
  campaignId: string;
  onRecorded: () => void;
}) {
  const [form, setForm] = useState({
    template_version: '',
    document_ref: '',
    signatory_name: '',
    signatory_email: '',
    signed_at: '',
  });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  function set(k: keyof typeof form, v: string) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await recordAgreement(campaignId, {
        ...form,
        // datetime-local has no timezone; send as ISO with a Z so the backend
        // treats it as UTC (its validator allows a small skew).
        signed_at: new Date(form.signed_at).toISOString(),
      });
      onRecorded();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not record the agreement.');
    } finally {
      setBusy(false);
    }
  }

  const field =
    'mt-1 w-full rounded-lg border border-line px-3 py-2 text-sm text-ink focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary';

  return (
    <form className="space-y-3" onSubmit={onSubmit}>
      <p className="text-xs text-ink-500">
        Record a REAL, attorney-approved signed agreement document. This unblocks
        go-live; it is not legal sign-off.
      </p>
      <label className="block text-sm font-medium text-ink-700">
        Template version
        <input className={field} required value={form.template_version}
          onChange={(e) => set('template_version', e.target.value)} />
      </label>
      <label className="block text-sm font-medium text-ink-700">
        Signed document reference (URL / storage ref)
        <input className={field} required value={form.document_ref}
          onChange={(e) => set('document_ref', e.target.value)} />
      </label>
      <label className="block text-sm font-medium text-ink-700">
        Signatory name
        <input className={field} required value={form.signatory_name}
          onChange={(e) => set('signatory_name', e.target.value)} />
      </label>
      <label className="block text-sm font-medium text-ink-700">
        Signatory email
        <input className={field} type="email" required value={form.signatory_email}
          onChange={(e) => set('signatory_email', e.target.value)} />
      </label>
      <label className="block text-sm font-medium text-ink-700">
        Signed at
        <input className={field} type="datetime-local" required value={form.signed_at}
          onChange={(e) => set('signed_at', e.target.value)} />
      </label>
      {error && <Banner kind="error">{error}</Banner>}
      <button type="submit" disabled={busy}
        className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-60">
        {busy ? 'Recording…' : 'Record signed agreement'}
      </button>
    </form>
  );
}

// corp-14-5/7: operator-only — external QR redirect + prize image.
function SetupConfigForm({
  campaignId,
  initial,
  onSaved,
}: {
  campaignId: string;
  initial: { qr_redirect_url: string | null; prize_media_url: string | null };
  onSaved: () => void;
}) {
  const [form, setForm] = useState({
    qr_redirect_url: initial.qr_redirect_url ?? '',
    prize_media_url: initial.prize_media_url ?? '',
  });
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState(false);
  const [busy, setBusy] = useState(false);
  const field =
    'mt-1 w-full rounded-lg border border-line px-3 py-2 text-sm text-ink focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary';

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setOk(false);
    setBusy(true);
    try {
      await updateSetupConfig(campaignId, {
        qr_redirect_url: form.qr_redirect_url.trim() || null,
        prize_media_url: form.prize_media_url.trim() || null,
      });
      setOk(true);
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save the setup config.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="space-y-3" onSubmit={onSubmit}>
      <p className="text-xs text-ink-500">
        Operator-only. The external redirect still records a scan on our tracker
        first (attribution kept), then forwards to the client URL. Leave blank to
        send scans to our competition page.
      </p>
      <label className="block text-sm font-medium text-ink-700">
        External QR redirect URL (optional)
        <input className={field} type="url" placeholder="https://client-site.example/win"
          value={form.qr_redirect_url}
          onChange={(e) => setForm((f) => ({ ...f, qr_redirect_url: e.target.value }))} />
      </label>
      <label className="block text-sm font-medium text-ink-700">
        Prize image URL (optional)
        <input className={field} type="url" placeholder="https://…/prize.png"
          value={form.prize_media_url}
          onChange={(e) => setForm((f) => ({ ...f, prize_media_url: e.target.value }))} />
      </label>
      {error && <Banner kind="error">{error}</Banner>}
      {ok && <Banner kind="ok">Saved.</Banner>}
      <button type="submit" disabled={busy}
        className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-60">
        {busy ? 'Saving…' : 'Save setup config'}
      </button>
    </form>
  );
}

// leads-pii: operator view of the campaign's CONSENTED, un-withdrawn leads —
// decrypted contact PII (name/email/phone) + a CSV download. The backend gates
// + audits every read, so this panel only ever shows consent-gated data.
function ConsentedLeadsPanel({ campaignId }: { campaignId: string }) {
  const [leads, setLeads] = useState<LeadExportItem[] | null>(null);
  const [count, setCount] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLeads(null);
    setError(null);
    try {
      const res = await getCampaignLeads(campaignId);
      setLeads(res.leads);
      setCount(res.count);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) return;
      setError(err instanceof ApiError ? err.message : 'Could not load the consented leads.');
    }
  }, [campaignId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function onDownload() {
    setDownloadError(null);
    setDownloading(true);
    try {
      await downloadCampaignLeadsCsv(campaignId);
    } catch (err) {
      setDownloadError(err instanceof ApiError ? err.message : 'Could not download the CSV.');
    } finally {
      setDownloading(false);
    }
  }

  return (
    <section className="rounded-2xl border border-line bg-white p-6 shadow-card">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <h2 className="text-base font-semibold text-ink">Consented leads</h2>
          {leads !== null && (
            <span
              className="rounded-full bg-surface-muted px-2 py-0.5 text-xs font-medium text-ink-700"
              aria-label={`${count} consented lead${count === 1 ? '' : 's'}`}
            >
              {count}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={onDownload}
          disabled={downloading || (leads !== null && count === 0)}
          className="inline-flex cursor-pointer items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <svg
            className="h-4 w-4"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
          </svg>
          {downloading ? 'Preparing…' : 'Download CSV'}
        </button>
      </div>

      <p className="mb-4 text-xs text-ink-500">
        Only leads with recorded, un-withdrawn consent appear here. Contact details
        are encrypted at rest and every access is audited (POPIA).
      </p>

      {downloadError && <Banner kind="error">{downloadError}</Banner>}
      {error && <Banner kind="error">{error}</Banner>}
      {!error && leads === null && (
        <p className="text-sm text-ink-500" role="status" aria-live="polite">
          Loading leads…
        </p>
      )}
      {!error && leads !== null && leads.length === 0 && (
        <p className="text-sm text-ink-500">
          No consented leads yet for this campaign.
        </p>
      )}

      {!error && leads !== null && leads.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[36rem] border-collapse text-sm">
            <caption className="sr-only">Consented leads for this campaign</caption>
            <thead>
              <tr className="border-b border-line text-left text-xs uppercase tracking-wide text-ink-500">
                <th scope="col" className="py-2 pr-4 font-medium">Name</th>
                <th scope="col" className="py-2 pr-4 font-medium">Email</th>
                <th scope="col" className="py-2 pr-4 font-medium">Phone</th>
                <th scope="col" className="py-2 font-medium">Captured</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {leads.map((lead) => (
                <tr key={lead.lead_id} className="text-ink">
                  <td className="py-2 pr-4">{lead.name || <span className="text-ink-500">—</span>}</td>
                  <td className="py-2 pr-4">
                    {lead.email ? (
                      <a href={`mailto:${lead.email}`} className="text-primary hover:underline">
                        {lead.email}
                      </a>
                    ) : (
                      <span className="text-ink-500">—</span>
                    )}
                  </td>
                  <td className="py-2 pr-4">{lead.phone || <span className="text-ink-500">—</span>}</td>
                  <td className="py-2 text-ink-500">
                    {new Date(lead.captured_at).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

export function AdminCampaignReviewPage() {
  const { id } = useParams<{ id: string }>();
  const [campaign, setCampaign] = useState<CampaignRead | null>(null);
  const [allowed, setAllowed] = useState<string[]>([]);
  const [agreement, setAgreement] = useState<AgreementRead | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busyVerb, setBusyVerb] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    setError(null);
    try {
      const [c, t] = await Promise.all([getCampaign(id), getAllowedTransitions(id)]);
      setCampaign(c);
      setAllowed(t.allowed_transitions);
      // Agreement may not exist yet (404) — that's fine.
      try {
        setAgreement(await getAgreement(id));
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) setAgreement(null);
      }
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) return;
      setError(err instanceof ApiError ? err.message : 'Could not load this campaign.');
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  async function runVerb(verb: string) {
    if (!id) return;
    if (DESTRUCTIVE_VERBS.has(verb) && !window.confirm(`${VERB_LABELS[verb]} this campaign?`)) {
      return;
    }
    setActionError(null);
    setBusyVerb(verb);
    try {
      await applyTransition(id, verb);
      await load();
    } catch (err) {
      // Surface backend guard reasons verbatim (e.g. the go-live agreement gate).
      setActionError(err instanceof ApiError ? err.message : `Could not ${verb}.`);
    } finally {
      setBusyVerb(null);
    }
  }

  async function onVoid() {
    if (!id || !window.confirm('Void the recorded agreement? This re-blocks go-live.')) return;
    try {
      await voidAgreement(id);
      await load();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'Could not void the agreement.');
    }
  }

  async function onConsent(record: boolean) {
    if (!id) return;
    if (!record && !window.confirm('Clear lead consent? This re-blocks go-live.')) return;
    try {
      await (record ? recordLeadConsent(id) : revokeLeadConsent(id));
      await load();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'Could not update lead consent.');
    }
  }

  const agreementSigned = agreement?.status === 'signed';
  const operatorActions = OPERATOR_VERBS.filter((v) => allowed.includes(v));

  return (
    <AdminShell title="Campaign review">
      <Link to="/admin" className="mb-4 inline-block text-sm text-primary hover:underline">
        ← Back to queue
      </Link>
      {error && <Banner kind="error">{error}</Banner>}
      {!error && !campaign && <p className="text-sm text-ink-500">Loading…</p>}
      {campaign && (
        <div className="space-y-6">
          <section className="rounded-2xl border border-line bg-white p-6 shadow-card">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-base font-semibold text-ink">{campaign.name}</h2>
              <span className="rounded-full bg-surface-muted px-2 py-0.5 text-xs font-medium text-ink-700">
                {campaign.status}
              </span>
            </div>
            <dl className="space-y-1 text-sm">
              <div className="flex justify-between border-b border-line py-1">
                <dt className="text-ink-500">Prize</dt>
                <dd className="text-ink">{campaign.prize_title ?? '—'}</dd>
              </div>
              <div className="flex justify-between border-b border-line py-1">
                <dt className="text-ink-500">Skill challenge</dt>
                <dd className="text-ink">{campaign.skill_challenge_type ?? '—'}</dd>
              </div>
              <div className="flex justify-between border-b border-line py-1">
                <dt className="text-ink-500">Cash-equivalent allowed</dt>
                <dd className="text-ink">{campaign.cash_equivalent_allowed ? 'Yes' : 'No'}</dd>
              </div>
              <div className="flex justify-between border-b border-line py-1">
                <dt className="text-ink-500">Agreement</dt>
                <dd className="text-ink">
                  {agreementSigned ? 'Signed (recorded)' : 'Not recorded'}
                </dd>
              </div>
              <div className="flex items-center justify-between py-1">
                <dt className="text-ink-500">Public competition URL</dt>
                <dd className="text-ink">
                  {campaign.public_url ? (
                    <CopyLink url={campaign.public_url} />
                  ) : (
                    <span className="text-ink-500">— (set once linked to prizes)</span>
                  )}
                </dd>
              </div>
            </dl>
          </section>

          {campaign.status === 'under_review' && (
            <section className="rounded-2xl border border-line bg-white p-6 shadow-card">
              <h2 className="mb-4 text-base font-semibold text-ink">Review checklist</h2>
              <ReviewChecklistForm campaignId={id!} onSaved={() => void load()} />
            </section>
          )}

          {actionError && <Banner kind="error">{actionError}</Banner>}

          <section className="rounded-2xl border border-line bg-white p-6 shadow-card">
            <h2 className="mb-4 text-base font-semibold text-ink">Workflow actions</h2>
            {operatorActions.length === 0 ? (
              <p className="text-sm text-ink-500">No actions available from this state.</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {operatorActions.map((verb) => (
                  <button
                    key={verb}
                    type="button"
                    disabled={busyVerb !== null}
                    onClick={() => runVerb(verb)}
                    className="rounded-lg border border-line px-3 py-1.5 text-sm font-medium text-ink-700 hover:bg-surface-muted disabled:opacity-60"
                  >
                    {busyVerb === verb ? '…' : VERB_LABELS[verb] ?? verb}
                  </button>
                ))}
              </div>
            )}
            {allowed.includes('go_live') && !agreementSigned && (
              <p className="mt-3 text-xs text-ink-500">
                Go-live requires a recorded signed agreement (epic-7 gate).
              </p>
            )}
          </section>

          <section className="rounded-2xl border border-line bg-white p-6 shadow-card">
            <h2 className="mb-4 text-base font-semibold text-ink">Agreement</h2>
            {agreementSigned ? (
              <div className="space-y-3 text-sm text-ink">
                <Banner kind="ok">
                  Signed agreement recorded — {agreement?.template_version} ·{' '}
                  {agreement?.signatory_name}
                </Banner>
                <button
                  type="button"
                  onClick={onVoid}
                  className="rounded-lg border border-red-200 px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-50"
                >
                  Void agreement
                </button>
              </div>
            ) : (
              <AgreementForm campaignId={id!} onRecorded={() => void load()} />
            )}
          </section>

          {campaign.leads_capture_enabled && (
            <section className="rounded-2xl border border-line bg-white p-6 shadow-card">
              <h2 className="mb-4 text-base font-semibold text-ink">Lead consent</h2>
              {campaign.lead_consent_recorded ? (
                <div className="space-y-3 text-sm text-ink">
                  <Banner kind="ok">
                    Lead consent recorded — partner PII is shared only on recorded consent.
                  </Banner>
                  <button
                    type="button"
                    onClick={() => onConsent(false)}
                    className="rounded-lg border border-red-200 px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-50"
                  >
                    Clear consent
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-xs text-ink-500">
                    This campaign captures leads. Confirm the campaign-specific consent
                    mechanism (consent text + entrant opt-in) is in place before going
                    live — required by the go-live compliance gate.
                  </p>
                  <button
                    type="button"
                    onClick={() => onConsent(true)}
                    className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:opacity-90"
                  >
                    Record lead consent
                  </button>
                </div>
              )}
            </section>
          )}

          {campaign.leads_capture_enabled && <ConsentedLeadsPanel campaignId={id!} />}

          <section className="rounded-2xl border border-line bg-white p-6 shadow-card">
            <h2 className="mb-4 text-base font-semibold text-ink">Setup config</h2>
            <SetupConfigForm
              campaignId={id!}
              initial={{
                qr_redirect_url: (campaign as unknown as { qr_redirect_url?: string | null }).qr_redirect_url ?? null,
                prize_media_url: (campaign as unknown as { prize_media_url?: string | null }).prize_media_url ?? null,
              }}
              onSaved={() => void load()}
            />
          </section>
        </div>
      )}
    </AdminShell>
  );
}

export default AdminCampaignReviewPage;
